/**
 * Modern Bold · Collection page (modern template)
 * ─────────────────────────────────────────────────────────────────────────
 * Behaviours:
 *   1. Filter drawer — open / close / focus-trap / Esc
 *   2. Quick-drop chips (sticky bar) — open / close / Esc / outside-click
 *   3. Sort dropdown — listbox pattern, arrow-key nav
 *   4. Quick-add — POST /cart/add.js → open cart drawer (1-variant cards)
 *   5. Price range slider — dual-thumb, live display update
 *   6. Filter links — AJAX section swap (no full reload); open quick-drop /
 *      drawer stay open while the product grid updates in the background.
 *
 * Wishlist lives in modern-wishlist.js (<mb-wishlist-button> custom
 * element) and is loaded by this section's template independently.
 *
 * No dependencies. ES2017+. IIFE pattern matches modern-bold.js.
 * Loaded only on collection.modern template — NOT added to theme.liquid.
 */
(function () {
  'use strict';

  // ── Helpers ─────────────────────────────────────────────────────────────

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /** Trap Tab/Shift-Tab focus inside `container`. Returns a cleanup fn. */
  function trapFocus(container) {
    const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
    function getEls() {
      return Array.from(container.querySelectorAll(focusable)).filter(
        (el) => !el.closest('[hidden]') && !el.closest('[inert]')
      );
    }
    function handler(e) {
      if (e.key !== 'Tab') return;
      const els = getEls();
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  function lockBody() {
    if (document.body.dataset.mbScrollLocked) return;
    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.dataset.mbScrollLocked = scrollY;
  }

  function unlockBody() {
    const scrollY = parseInt(document.body.dataset.mbScrollLocked || '0', 10);
    document.body.style.overflow = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.position = '';
    delete document.body.dataset.mbScrollLocked;
    window.scrollTo(0, scrollY);
  }

  // ── Quick-add ────────────────────────────────────────────────────────────

  const SECTIONS_TO_REFRESH = ['cart-drawer', 'cart-icon-bubble'];

  function refreshCartSections() {
    const params = SECTIONS_TO_REFRESH.map((s) => `sections=${encodeURIComponent(s)}`).join('&');
    const url = `${(window.routes && window.routes.cart_url) || '/cart'}?${params}&_=${Date.now()}`;
    return fetch(url, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((state) => {
        if (!state || !state.sections) return;
        const drawer = document.querySelector('cart-drawer');
        if (drawer && state.sections['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(state.sections['cart-drawer'], 'text/html');
          const fresh = parsed.querySelector('#CartDrawer');
          const current = drawer.querySelector('#CartDrawer');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          drawer.classList.remove('is-empty');
        }
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && state.sections['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html');
          const fresh = parsed.querySelector('#cart-icon-bubble');
          if (fresh) {
            const newCount = fresh.querySelector('span[aria-hidden="true"]');
            const oldCount = bubble.querySelector('span[aria-hidden="true"]');
            if (newCount && oldCount) oldCount.textContent = newCount.textContent;
            const al = fresh.getAttribute('aria-label');
            if (al) bubble.setAttribute('aria-label', al);
          }
        }
      });
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  function showCardError(article, message) {
    const el = article.querySelector('[data-mb-error]') || article.querySelector('.mb-colcard__error');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    setTimeout(() => { el.hidden = true; el.textContent = ''; }, 4000);
  }

  function handleQuickAdd(btn) {
    if (btn.disabled) return;
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    const article = btn.closest('[data-handle]');
    btn.disabled = true;
    btn.classList.add('is-loading');

    const body = JSON.stringify({
      items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      sections: SECTIONS_TO_REFRESH.join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res && res.sections) {
          // Inline refresh from the response (saves a round-trip, same pattern as modern-cart-drawer.js)
          const drawer = document.querySelector('cart-drawer');
          if (drawer && res.sections['cart-drawer']) {
            const parsed = new DOMParser().parseFromString(res.sections['cart-drawer'], 'text/html');
            const fresh = parsed.querySelector('#CartDrawer');
            const current = drawer.querySelector('#CartDrawer');
            if (fresh && current) current.innerHTML = fresh.innerHTML;
            drawer.classList.remove('is-empty');
          }
          openCartDrawer();
        } else if (res && res.status && res.description) {
          // Shopify error object
          if (article) showCardError(article, res.description);
        } else {
          return refreshCartSections().then(openCartDrawer);
        }
      })
      .catch(() => {
        if (article) showCardError(article, 'Could not add to cart. Please try again.');
      })
      .finally(() => {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      });
  }

  function initQuickAdd(root) {
    root.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-mb-quick-add]');
      if (!btn) return;
      e.preventDefault();
      handleQuickAdd(btn);
    });
  }

  // ── Filter drawer ────────────────────────────────────────────────────────

  let filterDrawerCleanup = null;

  function openFilterDrawer(root) {
    const drawer = root.querySelector('[data-mb-filter-drawer]');
    const overlay = root.querySelector('[data-mb-filter-overlay]');
    const trigger = root.querySelector('[data-mb-open-filter-drawer]');
    if (!drawer) return;

    drawer.hidden = false;
    // Force reflow then animate
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
    });
    document.body.setAttribute('aria-hidden', 'false');
    lockBody();
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    // A11y: mark the rest of the page as `inert` so:
    //  1) assistive-tech can't navigate to footer/header/main while
    //     the dialog is modal (matches the visual modality)
    //  2) axe-core stops scanning content under the overlay (avoids
    //     the false-positive color-contrast hits caused by the dark
    //     overlay shading the footer text below)
    //  3) keyboard tab is naturally trapped inside the drawer
    setSiblingsInert(drawer, true);

    // Focus first focusable element inside drawer
    const firstFocusable = drawer.querySelector('button,input,a,[tabindex]:not([tabindex="-1"])');
    if (firstFocusable) requestAnimationFrame(() => firstFocusable.focus());

    filterDrawerCleanup = trapFocus(drawer);
  }

  // Walk up from `el` to <body>, marking every direct sibling at each
  // level as `inert` (or removing it). This fully isolates the modal.
  function setSiblingsInert(el, on) {
    let cur = el;
    while (cur && cur.parentElement && cur !== document.body) {
      [...cur.parentElement.children].forEach((sib) => {
        if (sib === cur) return;
        if (on) {
          if (!sib.hasAttribute('data-mb-was-inert')) {
            sib.setAttribute('data-mb-was-inert', sib.hasAttribute('inert') ? '1' : '0');
            sib.setAttribute('inert', '');
          }
        } else if (sib.hasAttribute('data-mb-was-inert')) {
          if (sib.getAttribute('data-mb-was-inert') === '0') sib.removeAttribute('inert');
          sib.removeAttribute('data-mb-was-inert');
        }
      });
      cur = cur.parentElement;
    }
  }

  function closeFilterDrawer(root) {
    const drawer = root.querySelector('[data-mb-filter-drawer]');
    const overlay = root.querySelector('[data-mb-filter-overlay]');
    const trigger = root.querySelector('[data-mb-open-filter-drawer]');
    if (!drawer) return;

    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');

    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (filterDrawerCleanup) { filterDrawerCleanup(); filterDrawerCleanup = null; }
    unlockBody();
    // Restore inert state on the rest of the page
    setSiblingsInert(drawer, false);

    // Wait for CSS transition then hide
    drawer.addEventListener('transitionend', function hide() {
      drawer.hidden = true;
      drawer.removeEventListener('transitionend', hide);
    }, { once: true });

    // Restore focus to trigger
    if (trigger) trigger.focus();
  }

  function initFilterDrawer(root) {
    const openBtn = root.querySelector('[data-mb-open-filter-drawer]');
    const drawer  = root.querySelector('[data-mb-filter-drawer]');
    const overlay = root.querySelector('[data-mb-filter-overlay]');

    if (!openBtn || !drawer) return;

    openBtn.addEventListener('click', () => openFilterDrawer(root));

    // Close button inside drawer
    drawer.querySelectorAll('[data-mb-close-filter-drawer]').forEach((btn) => {
      btn.addEventListener('click', () => closeFilterDrawer(root));
    });

    // Overlay click
    if (overlay) overlay.addEventListener('click', () => closeFilterDrawer(root));

    // Escape is bound once globally in bindDocumentUi() — live-query so it
    // still works after AJAX section swaps replace this drawer node.
  }

  // ── Quick-drop chips (sticky bar) ────────────────────────────────────────

  /**
   * Position a fixed panel so its top-left aligns with the bottom of `trigger`.
   * Clamps left so the panel never overflows the right edge of the viewport.
   * Used for both quick-drop panels and the sort panel.
   */
  function positionFixedPanel(panel, trigger, alignRight) {
    const rect = trigger.getBoundingClientRect();
    const panelW = panel.offsetWidth || parseInt(getComputedStyle(panel).minWidth, 10) || 200;
    const GAP = 8;
    const BOTTOM_GAP = 24;   // air between the panel and the viewport edge
    const MIN_H = 160;       // never collapse the list to an unusable sliver
    const MAX_H = 360;       // design cap, mirrors the CSS rule
    const top = rect.bottom + GAP;

    // Height is capped against the panel's ACTUAL top, not a fixed guess. The
    // CSS fallback (100vh - 120px) assumed the bar sat near the top of the
    // viewport; wherever it sits lower, the list ran flush into the bottom
    // edge with no breathing room.
    const available = window.innerHeight - top - BOTTOM_GAP;
    panel.style.maxHeight = `${Math.max(MIN_H, Math.min(MAX_H, available))}px`;
    let left;
    if (alignRight) {
      left = rect.right - panelW;
    } else {
      left = rect.left;
    }
    // Clamp so panel doesn't escape viewport on either side
    const vw = window.innerWidth;
    left = Math.min(left, vw - panelW - 16);
    left = Math.max(left, 16);
    panel.style.top  = `${top}px`;
    panel.style.left = `${left}px`;
  }

  function closeAllQuickDrops(except) {
    document.querySelectorAll('[data-mb-quick-drop].is-open').forEach((d) => {
      if (except && d === except) return;
      const panel = d.querySelector('[data-quick-drop-panel], .mb-collection__quick-panel');
      const trigger = d.querySelector('[data-quick-drop-trigger]');
      if (panel) panel.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      d.classList.remove('is-open');
    });
  }

  function openQuickDrop(drop) {
    if (!drop) return;
    const trigger = drop.querySelector('[data-quick-drop-trigger]');
    const panel = drop.querySelector('.mb-collection__quick-panel');
    if (!trigger || !panel) return;
    closeAllQuickDrops(drop);
    panel.hidden = false;
    positionFixedPanel(panel, trigger, false);
    trigger.setAttribute('aria-expanded', 'true');
    drop.classList.add('is-open');
  }

  function initQuickDrops(root) {
    const drops = root.querySelectorAll('[data-mb-quick-drop]');
    if (!drops.length) return;

    drops.forEach((drop) => {
      const trigger = drop.querySelector('[data-quick-drop-trigger]');
      const panel = drop.querySelector('.mb-collection__quick-panel');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        const isOpen = !panel.hidden;
        closeAllQuickDrops();
        if (!isOpen) openQuickDrop(drop);
      });
    });
  }

  // ── Sort dropdown ────────────────────────────────────────────────────────

  function initSort(root) {
    const wrap    = root.querySelector('[data-mb-sort-wrap]');
    if (!wrap) return;
    const trigger = wrap.querySelector('[data-mb-sort-trigger]');
    const panel   = wrap.querySelector('[data-mb-sort-option]')?.closest('[role="listbox"]');
    const label   = wrap.querySelector('[data-mb-sort-label]');
    if (!trigger || !panel) return;

    function openSort() {
      panel.hidden = false;
      // Align the fixed panel so its right edge lines up with the trigger's
      // right edge (sort button sits at the far right of the filter bar).
      positionFixedPanel(panel, trigger, true);
      trigger.setAttribute('aria-expanded', 'true');
      const active = panel.querySelector('[aria-selected="true"]');
      if (active) active.focus();
    }

    function closeSort() {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      panel.hidden ? openSort() : closeSort();
    });

    panel.addEventListener('click', function (e) {
      const option = e.target.closest('[data-mb-sort-option]');
      if (!option) return;
      selectSort(option);
    });

    // Arrow key navigation within listbox
    panel.addEventListener('keydown', function (e) {
      const options = Array.from(panel.querySelectorAll('[data-mb-sort-option]'));
      const current = document.activeElement;
      const idx = options.indexOf(current);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = options[(idx + 1) % options.length];
        if (next) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = options[(idx - 1 + options.length) % options.length];
        if (prev) prev.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (current && current.hasAttribute('data-mb-sort-option')) selectSort(current);
      } else if (e.key === 'Escape') {
        closeSort();
        trigger.focus();
      }
    });

    function selectSort(option) {
      const value = option.getAttribute('data-sort-value');
      if (!value) return;
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      // A new sort order re-shuffles the whole result set, so the page the
      // customer happened to be on is meaningless afterwards — send them back
      // to the start. Dropping the param (rather than setting page=1) keeps
      // the canonical first-page URL clean, same as the pagination links.
      url.searchParams.delete('page');
      closeSort();
      renderCollectionFromUrl(url.toString(), { keepPanelOpen: false });
    }

    // Outside click / Escape handled once in bindDocumentUi().
  }

  // Price range: visible inputs ARE the wire inputs (filter.v.price.gte/lte
  // takes whole dollars). No JS sync required — kept as a no-op stub so
  // existing initRoot() calls remain stable.
  function initPriceRange(/* root */) {}

  // ── Live product count in drawer footer ──────────────────────────────────

  const updateDrawerCount = debounce(function () {
    const form = document.querySelector('[data-mb-drawer-form]');
    const countEl = document.querySelector('[data-mb-drawer-product-count]');
    const activeEl = document.querySelector('[data-mb-drawer-active-count]');
    if (!form || !countEl) return;

    const params = new URLSearchParams(new FormData(form));
    // Strip empty params
    for (const [k, v] of [...params.entries()]) {
      if (v === '') params.delete(k);
    }
    const url = `${form.action || window.location.pathname}?${params.toString()}&_mb_count=1`;

    fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const countNode = doc.querySelector('[data-mb-collection]');
        if (!countNode) return;
        // Try to extract products_count from a meta tag or data attr we can read
        const grid = doc.querySelector('[data-collection-count]');
        if (grid) countEl.textContent = grid.getAttribute('data-collection-count');

        // Also update the badge count
        if (activeEl) {
          let activeCount = 0;
          params.forEach((v, k) => {
            if (k !== 'sort_by' && v) activeCount++;
          });
          activeEl.textContent = activeCount;
        }
      })
      .catch(() => {});
  }, 600);

  function initDrawerForm(root) {
    const form = root.querySelector('[data-mb-drawer-form]');
    if (!form) return;
    form.addEventListener('change', updateDrawerCount);
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const params = new URLSearchParams(new FormData(form));
      for (const [k, v] of [...params.entries()]) {
        if (v === '') params.delete(k);
      }
      // Keep the modern template suffix when this page was loaded with it.
      const current = new URL(window.location.href);
      if (current.searchParams.get('view') && !params.get('view')) {
        params.set('view', current.searchParams.get('view'));
      }
      params.delete('page');
      const action = form.getAttribute('action') || window.location.pathname;
      const url = `${action}?${params.toString()}`;
      renderCollectionFromUrl(url, { keepPanelOpen: true, restoreDrawer: true });
    });
  }

  // ── AJAX filter / sort / pagination (keep panels open) ───────────────────

  const FILTER_LINK_SELECTOR = [
    'a.mb-collection__quick-option',
    'a.mb-collection__swatch-item',
    'a.mb-collection__active-chip',
    'a.mb-collection__clear-all',
    'a.mb-filter-drawer__option',
    'a.mb-filter-drawer__swatch-item',
    'a.mb-filter-drawer__toggle-label',
    'a[data-mb-drawer-clear]',
    '.mb-collection__pagination-wrap a[href]',
  ].join(',');

  let filterFetchAbort = null;
  let documentUiBound = false;
  let popstateBound = false;
  let quizBannerTrackingBound = false;

  function ensureTemplateView(url) {
    const next = new URL(url, window.location.origin);
    const current = new URL(window.location.href);
    const view = current.searchParams.get('view');
    if (view && !next.searchParams.get('view')) next.searchParams.set('view', view);
    return next;
  }

  function captureFilterUiState(root) {
    const openTrigger = root.querySelector('[data-quick-drop-trigger][aria-expanded="true"]');
    const openPanelId = openTrigger ? openTrigger.getAttribute('aria-controls') : null;
    const openPanel = openPanelId ? document.getElementById(openPanelId) : null;
    const openLabel = openTrigger
      ? (openTrigger.querySelector('span')?.textContent || '').trim()
      : '';
    const drawer = root.querySelector('[data-mb-filter-drawer]');
    return {
      openPanelId,
      openLabel,
      panelScroll: openPanel ? openPanel.scrollTop : 0,
      drawerWasOpen: !!(drawer && drawer.classList.contains('is-open')),
    };
  }

  function restoreFilterUiState(root, state, { keepPanelOpen, restoreDrawer, focusLabel }) {
    if (keepPanelOpen) {
      let trigger = state.openPanelId
        ? root.querySelector(`[aria-controls="${CSS.escape(state.openPanelId)}"]`)
        : null;
      if (!trigger && state.openLabel) {
        root.querySelectorAll('[data-quick-drop-trigger]').forEach((t) => {
          const label = (t.querySelector('span')?.textContent || '').trim();
          if (label === state.openLabel) trigger = t;
        });
      }
      if (trigger) {
        const drop = trigger.closest('[data-mb-quick-drop]');
        openQuickDrop(drop);
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (panel) {
          panel.scrollTop = state.panelScroll || 0;
          if (focusLabel) {
            const match = Array.from(panel.querySelectorAll('.mb-collection__quick-option, .mb-collection__swatch-item'))
              .find((el) => {
                const text = (
                  el.querySelector('.mb-collection__check-label, .mb-collection__swatch-name')?.textContent ||
                  el.getAttribute('title') ||
                  ''
                ).trim();
                return text === focusLabel;
              });
            if (match) match.focus();
          }
        }
      }
    }

    if (restoreDrawer && state.drawerWasOpen) {
      openFilterDrawer(root);
    }
  }

  /**
   * Pagination swaps the grid in place, which leaves the reader parked at the
   * bottom of the page staring at the pagination row of a page they have not
   * seen. A real page load would have started them at the top; this restores
   * that. Filter clicks deliberately do NOT call this — there the reader is at
   * the filter bar watching the count change, and yanking the page would lose
   * their place.
   *
   * Focus follows the scroll: without it a keyboard user is scrolled to new
   * products while their focus is still on a pagination link that no longer
   * exists, and the next Tab starts from the top of the document.
   */
  function scrollToCollectionTop(root) {
    const target =
      root.querySelector('[data-mb-filter-bar]') ||
      root.querySelector('.mb-collection__grid') ||
      root;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = target.getBoundingClientRect().top + window.scrollY - 16;
    window.scrollTo({ top: Math.max(top, 0), behavior: reduced ? 'auto' : 'smooth' });

    const grid = root.querySelector('.mb-collection__grid');
    if (!grid) return;
    // tabindex is temporary: it exists only to receive this programmatic
    // focus, and is removed on blur so the grid never collects a Tab stop.
    grid.setAttribute('tabindex', '-1');
    grid.addEventListener('blur', () => grid.removeAttribute('tabindex'), { once: true });
    grid.focus({ preventScroll: true });
  }

  function renderCollectionFromUrl(urlString, options = {}) {
    const {
      keepPanelOpen = false,
      restoreDrawer = false,
      pushState = true,
      focusLabel = '',
      scrollToResults = false,
    } = options;

    const url = ensureTemplateView(urlString);
    const root = document.querySelector('[data-mb-collection]');
    if (!root) {
      window.location.assign(url.toString());
      return Promise.resolve();
    }

    const uiState = captureFilterUiState(root);

    // Tear down drawer lock/trap before we discard its DOM.
    if (uiState.drawerWasOpen) {
      if (filterDrawerCleanup) {
        filterDrawerCleanup();
        filterDrawerCleanup = null;
      }
      const oldDrawer = root.querySelector('[data-mb-filter-drawer]');
      if (oldDrawer) setSiblingsInert(oldDrawer, false);
      unlockBody();
    }

    root.classList.add('is-filtering');
    root.setAttribute('aria-busy', 'true');

    if (filterFetchAbort) filterFetchAbort.abort();
    filterFetchAbort = new AbortController();

    return fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: filterFetchAbort.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error('filter fetch failed');
        return r.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-mb-collection]');
        if (!fresh) throw new Error('filter section missing');

        root.innerHTML = fresh.innerHTML;
        root.className = fresh.className;
        if (fresh.id) root.id = fresh.id;

        delete root.dataset.mbCollectionInit;
        initCollection(root);

        restoreFilterUiState(root, uiState, {
          keepPanelOpen,
          restoreDrawer,
          focusLabel,
        });

        patchInStockCounts(root, url.toString());

        if (scrollToResults) scrollToCollectionTop(root);

        if (pushState) {
          history.pushState({ mbCollectionAjax: true }, '', url.toString());
        }
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        window.location.assign(url.toString());
      })
      .finally(() => {
        root.classList.remove('is-filtering');
        root.removeAttribute('aria-busy');
      });
  }

  function initAjaxFilters(root) {
    root.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const link = e.target.closest(FILTER_LINK_SELECTOR);
      if (!link || !link.getAttribute('href')) return;

      let url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (_) {
        return;
      }
      if (url.origin !== window.location.origin) return;

      e.preventDefault();

      const inQuickDrop = !!link.closest('[data-mb-quick-drop]');
      const inDrawer = !!link.closest('[data-mb-filter-drawer]');
      const focusLabel = (
        link.querySelector('.mb-collection__check-label, .mb-collection__swatch-name, .mb-filter-drawer__check-label, .mb-filter-drawer__swatch-name')?.textContent ||
        link.getAttribute('title') ||
        ''
      ).trim();

      renderCollectionFromUrl(url.toString(), {
        keepPanelOpen: inQuickDrop,
        restoreDrawer: inDrawer,
        focusLabel: inQuickDrop ? focusLabel : '',
        scrollToResults: !!link.closest('.mb-collection__pagination-wrap'),
      });
    });
  }

  function bindDocumentUi() {
    if (documentUiBound) return;
    documentUiBound = true;

    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-mb-quick-drop]')) closeAllQuickDrops();
      if (!e.target.closest('[data-mb-sort-wrap]')) {
        document.querySelectorAll('[data-mb-sort-wrap] [role="listbox"]:not([hidden])').forEach((panel) => {
          panel.hidden = true;
          const trigger = panel.closest('[data-mb-sort-wrap]')?.querySelector('[data-mb-sort-trigger]');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;

      const openDrawer = document.querySelector('[data-mb-filter-drawer].is-open');
      if (openDrawer) {
        const root = openDrawer.closest('[data-mb-collection]');
        if (root) closeFilterDrawer(root);
        return;
      }

      closeAllQuickDrops();

      document.querySelectorAll('[data-mb-sort-wrap] [role="listbox"]:not([hidden])').forEach((panel) => {
        panel.hidden = true;
        const trigger = panel.closest('[data-mb-sort-wrap]')?.querySelector('[data-mb-sort-trigger]');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      });
    });
  }

  /**
   * GA4: fire `rug_finder_click` when the rug finder banner is clicked.
   *
   * Delegated on document rather than bound to the element: filtering swaps
   * the grid's innerHTML (see renderCollectionFromUrl), which would throw away
   * a listener attached to the banner itself.
   *
   * `source` looks redundant with only one placement, but it keeps the numbers
   * separable if the same CTA later appears in the header or on a PDP —
   * without it every surface collapses into one count.
   *
   * GA4 sends over sendBeacon, so the event survives the navigation the click
   * starts. Falls back to a bare dataLayer push if only GTM is present.
   * Note gtag is absent on the local dev server (Shopify does not inject the
   * Google tag there) — the guard makes that a no-op, not an error.
   */
  function bindQuizBannerTracking() {
    if (quizBannerTrackingBound) return;
    quizBannerTrackingBound = true;

    document.addEventListener('click', function (e) {
      const banner = e.target.closest('[data-mb-quiz-banner]');
      if (!banner) return;
      const payload = {
        source: banner.dataset.source || 'collection_banner',
        collection: banner.dataset.collection || '',
      };
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'rug_finder_click', payload);
        } else if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push(Object.assign({ event: 'rug_finder_click' }, payload));
        }
      } catch { /* analytics must never break the navigation */ }
    });
  }

  function bindPopstate() {
    if (popstateBound) return;
    popstateBound = true;
    window.addEventListener('popstate', function () {
      if (!document.querySelector('[data-mb-collection]')) return;
      renderCollectionFromUrl(window.location.href, {
        keepPanelOpen: false,
        restoreDrawer: false,
        pushState: false,
      });
    });
  }

  // ── Main init ────────────────────────────────────────────────────────────

  // ── Honest counts for variant filters ────────────────────────────────────

  /**
   * Variant filter links carry filter.v.availability=1 (see
   * snippets/modern-filter-url.liquid), so the count Shopify rendered beside
   * them describes a result set the click will never produce: "8' x 10'
   * (1299)" lands on 741 products. Re-fetch the page with availability
   * applied and write in the number the click actually returns.
   *
   * Only runs while availability is absent from the URL — once it is applied
   * Shopify computes every count against it and the markup is already honest,
   * so this is at most one extra request per visit. Product filters
   * (filter.p.*) are untouched: their links do not add availability, so their
   * counts were never wrong.
   *
   * Known gap: a value whose in-stock count is 0 renders as a disabled <span>
   * in the fetched page, which carries no href to key on, so it keeps its
   * stale number. Measured 0 of 46 values on /collections/all; the safe
   * failure is a stale count, not a wrongly blanked list.
   *
   * Failure is silent by design — the counts already on the page are the
   * server's truth for the unfiltered set, and nothing here is interactive.
   * The numbers are not announced when they change: these are supplementary
   * figures inside links, and a live region firing forty times would bury
   * anything a screen reader user actually asked for.
   */
  const STOCK_PARAM = 'filter.v.availability';
  const COUNT_SELECTOR = '.mb-collection__check-count,.mb-filter-drawer__check-count';
  const stockCountCache = new Map();
  let stockCountAbort = null;

  function variantFacetsOf(urlString) {
    const set = new Set();
    new URL(urlString, window.location.origin).searchParams.forEach((value, key) => {
      if (key !== STOCK_PARAM && key.indexOf('filter.v.') === 0) set.add(`${key}=${value}`);
    });
    return set;
  }

  /**
   * The one variant facet an option link ADDS to the current set — the pair
   * that identifies the option. Works on both documents: the fetched page
   * carries the same facets plus availability, which is skipped here.
   */
  function addedVariantFacet(href, baseFacets) {
    let url;
    try { url = new URL(href, window.location.origin); } catch (_) { return null; }
    for (const [key, value] of url.searchParams) {
      if (key === STOCK_PARAM || key.indexOf('filter.v.') !== 0) continue;
      const pair = `${key}=${value}`;
      if (!baseFacets.has(pair)) return pair;
    }
    return null;
  }

  function readOptionCount(el) {
    const node = el.querySelector(COUNT_SELECTOR);
    const text = node ? node.textContent : el.getAttribute('aria-label') || '';
    const match = text.match(/\((\d+)\)\s*$/);
    return match ? match[1] : null;
  }

  function writeOptionCount(el, count) {
    const node = el.querySelector(COUNT_SELECTOR);
    if (node) node.textContent = `(${count})`;
    // Swatches carry their count in aria-label only — there is no count node.
    const label = el.getAttribute('aria-label');
    if (label && /\(\d+\)\s*$/.test(label)) {
      el.setAttribute('aria-label', label.replace(/\(\d+\)\s*$/, `(${count})`));
    }
  }

  function patchInStockCounts(root, urlString) {
    const current = new URL(urlString, window.location.origin);
    if (current.searchParams.has(STOCK_PARAM)) return;

    const baseFacets = variantFacetsOf(current.href);
    const targets = new Map();
    root.querySelectorAll('a[href*="filter.v."]').forEach((link) => {
      const href = link.getAttribute('href') || '';
      if (href.indexOf(`${STOCK_PARAM}=1`) === -1) return;
      const key = addedVariantFacet(href, baseFacets);
      if (!key) return;
      if (!targets.has(key)) targets.set(key, []);
      targets.get(key).push(link);
    });
    if (!targets.size) return;

    // Filter counts come from collection.filters, not from the products on
    // the page, so we ask for a page that cannot exist and get the whole
    // filter UI with an empty grid: 626 KB → 181 KB on /collections/all.
    // Section Rendering drops the layout on top of that. Both are also what
    // keeps the cache key stable across pagination.
    const fetchUrl = new URL(current.href);
    fetchUrl.searchParams.set(STOCK_PARAM, '1');
    fetchUrl.searchParams.set('page', '9999');
    const sectionId = root.closest('.shopify-section')?.id.replace('shopify-section-', '');
    if (sectionId) fetchUrl.searchParams.set('section_id', sectionId);
    const cacheKey = fetchUrl.toString();

    function apply(counts) {
      targets.forEach((links, key) => {
        const count = counts.get(key);
        if (count === undefined) return;
        links.forEach((link) => writeOptionCount(link, count));
      });
    }

    if (stockCountCache.has(cacheKey)) {
      apply(stockCountCache.get(cacheKey));
      return;
    }

    function run() {
      if (stockCountAbort) stockCountAbort.abort();
      stockCountAbort = new AbortController();

      fetch(cacheKey, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: stockCountAbort.signal,
      })
        .then((r) => {
          if (!r.ok) throw new Error('stock count fetch failed');
          return r.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const counts = new Map();
          doc.querySelectorAll('a[href*="filter.v."]').forEach((link) => {
            const key = addedVariantFacet(link.getAttribute('href') || '', baseFacets);
            if (!key || counts.has(key)) return;
            const count = readOptionCount(link);
            if (count !== null) counts.set(key, count);
          });
          if (!counts.size) return;
          stockCountCache.set(cacheKey, counts);
          apply(counts);
        })
        .catch(() => {});
    }

    // Idle: the filter panels are closed at this point, so the numbers settle
    // before anyone can be reading them, and the fetch never competes with
    // the grid's own images for bandwidth.
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 300);
  }

  function initCollection(root) {
    if (root.dataset.mbCollectionInit) return;
    root.dataset.mbCollectionInit = '1';

    initQuickAdd(root);
    initFilterDrawer(root);
    initQuickDrops(root);
    initSort(root);
    initPriceRange(root);
    initDrawerForm(root);
    initAjaxFilters(root);
    initClosePanelsOnScroll(root);
  }

  // Close any open quick-drop / sort panel as soon as the page scrolls.
  //
  // The panels are position: fixed with coordinates written once, on open —
  // they have to be fixed, because .mb-collection__filter-bar-inner is
  // overflow-x: auto and would clip an absolutely positioned child. Fixed
  // coords mean that on scroll the panel either sits parked in mid-air over
  // the product grid or, if we re-position it every frame, slides along with
  // the page. Neither reads as attached to its chip, so the panel closes
  // instead. Matches the "no sticky filter bar" decision documented in
  // modern-collection.css.
  //
  // Bound once: AJAX filter swaps re-run initCollection, and stacking
  // scroll listeners would close the restored panel on the tiniest jitter.
  let scrollCloseBound = false;
  function initClosePanelsOnScroll(/* root */) {
    if (scrollCloseBound) return;
    scrollCloseBound = true;

    let lastY = window.scrollY;

    window.addEventListener('scroll', () => {
      const dy = Math.abs(window.scrollY - lastY);
      lastY = window.scrollY;
      // Ignore sub-pixel jitter and iOS rubber-banding.
      if (dy < 4) return;

      document
        .querySelectorAll('[data-quick-drop-trigger][aria-expanded="true"], [data-mb-sort-trigger][aria-expanded="true"]')
        .forEach((trigger) => {
          const panelId = trigger.getAttribute('aria-controls');
          const panel = panelId ? document.getElementById(panelId) : null;
          if (!panel || panel.hidden) return;
          // A11y: never hide the element holding focus without handing focus
          // somewhere sensible — a keyboard user arrowing through the options
          // would otherwise land on <body>.
          if (panel.contains(document.activeElement)) trigger.focus();
          panel.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
          trigger.closest('[data-mb-quick-drop]')?.classList.remove('is-open');
        });
    }, { passive: true });
  }

  function initAll() {
    bindDocumentUi();
    bindPopstate();
    bindQuizBannerTracking();
    document.querySelectorAll('[data-mb-collection]').forEach((root) => {
      initCollection(root);
      patchInStockCounts(root, window.location.href);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify theme editor section loads
  document.addEventListener('shopify:section:load', initAll);
})();
